import Masonry from 'react-smart-masonry';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled from 'styled-components';
import { Mixins, ScreenWidth, ScreenWidthValues, Spacing } from 'web-common/src/shared/styles';
import { PageHeader } from '../PageHeader';
import { Panel } from '../Panel';
import { TwoColumn } from './TwoColumn';

/**
 * Defines Page component for Shef Dashboard.
 * Typical layout as follows:
 *  <Page>
 *    // Components that are full page width.
 *    // Note: Anything placed within Page.Grid will automatically resize to grid layout.
 *
 *    <Page.Grid> -- Auto arranges items
 *      <Page.Grid.Column>
 *        <Page.Grid.Column.Full>
 *          // Components that take a full column width
 *        <Page.Grid.Column.Full>
 *        <Page.Grid.Column.Half>
 *          // Components that take half a column width
 *        <Page.Grid.Column.Half>
 *      <Page.Grid.Column>
 *    <Page.Grid>
 *  </Page>
 */

// Page.Grid.Column.Half
export const ColumnHalfComponent = styled.div`
  flex: 1 1 25%;
`;

// Page.Grid.Column.Full
export const ColumnFullComponent = styled.div`
  flex: 1 1 100%;
  width: 100%;
`;

// Page.Grid.Column
export const ColumnComponent = styled.div`
  display: flex;
  gap: ${Spacing.SINGLE};
  flex-wrap: wrap;

  &:not(:last-child) {
    margin-bottom: ${Spacing.SINGLE};
  }
`;

// Page.Span
export const Span = styled.div`
  width: 100%;
  margin-bottom: ${Spacing.SINGLE};
`;

// Page.Grid
export const GridComponent = styled(Masonry).attrs(() => ({
  breakpoints: {
    desktop: ScreenWidthValues[ScreenWidth.MEDIUM],
    mobile: ScreenWidthValues[ScreenWidth.SMALL],
  },
  columns: {
    desktop: 2,
    mobile: 1,
  },
  gap: Spacing.SINGLE,
}))`
  display: flex;

  ${Mixins.Media.maxWidthMedium`
    display: block;
  `}
`;

export const PageComponent = styled.div<{ fullWidth?: boolean }>`
  max-width: ${ifProp('fullWidth', '100%', `800px`)};
  margin: auto;

  ${Mixins.Media.maxWidthLarge(`
    max-width: 800px;
  `)}

  ${Mixins.Media.maxWidthSmall(`
    padding: 0 ${Spacing.SINGLE};
  `)}
`;

// eslint-disable-next-line functional/immutable-data -- Assign Column Component
export const Column = Object.assign(ColumnComponent, { Half: ColumnHalfComponent, Full: ColumnFullComponent });

// eslint-disable-next-line functional/immutable-data -- Assign Grid Component
export const Grid = Object.assign(GridComponent, { Column });

// eslint-disable-next-line functional/immutable-data -- Assign Page Component
export const Page = Object.assign(PageComponent, { Grid, Header: PageHeader, Panel, Span, TwoColumn });
